import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@shared/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { ThemeToggle } from '@shared/ui/theme-toggle';
import { LanguageSwitcher } from '@shared/i18n/LanguageSwitcher';
import { User, Settings, LogOut, Calendar, Building, ChevronDown, Bell } from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import { useAuth } from '@shared/hooks/useAuth';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';
import { localizePath, useLocale } from '@shared/i18n';

interface DoctorMobileHeaderProps {
  user: UserProfile;
  className?: string;
}

/**
 * Compact mobile header for doctor portal
 *
 * Features:
 * - Sticky positioning with backdrop blur
 * - Jema logo
 * - User avatar dropdown with profile actions
 * - Quick access to Calendar and Templates
 * - Theme toggle and language switcher
 * - Accessible with proper ARIA labels
 */
export const DoctorMobileHeader: React.FC<DoctorMobileHeaderProps> = ({ user, className }) => {
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { i18n } = useLingui();
  const { signOut } = useAuth();
  const { isEffectiveOrganizationOwner, normalizedPathname, resolveDoctorPath } =
    useDoctorWorkspace();
  const unreadCount = 0;

  const isOwner = isEffectiveOrganizationOwner;

  // Fetch profile photo with caching
  // Memoize user display info to prevent recalculations
  const userInfo = React.useMemo(() => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const displayName = fullName || user.email?.split('@')[0] || 'Doctor';
    const initials =
      displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'DR';
    const fallbackAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`;

    return {
      displayName,
      initials,
      email: user.email || '',
      avatarSrc: fallbackAvatar,
    };
  }, [user.first_name, user.last_name, user.email]);

  // Sign out handler with error handling
  const handleSignOut = React.useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
      // Could show a toast notification here
    }
  }, [signOut]);

  // Navigation helper
  const handleNavigate = React.useCallback(
    (path: string) => {
      navigate(localizePath(path, locale));
    },
    [locale, navigate]
  );

  // Get current page title for header
  const pageTitle = React.useMemo(() => {
    switch (normalizedPathname) {
      case '/dashboard':
        return i18n._({
          id: 'shared.lib.navigation.doctor.my-day',
          message: 'My Day',
        });
      case '/patients':
        return i18n._({
          id: 'shared.lib.navigation.doctor.patients',
          message: 'Patients',
        });
      case '/quotes':
        return i18n._({
          id: 'shared.lib.navigation.doctor.quotes',
          message: 'Quotes',
        });
      case '/calendar':
        return i18n._({
          id: 'shared.lib.navigation.doctor.calendar',
          message: 'Calendar',
        });
      case '/payments':
        return i18n._({
          id: 'shared.lib.navigation.doctor.payments',
          message: 'Payments',
        });
      case '/doctor-profile':
        return i18n._({
          id: 'shared.lib.navigation.doctor.profile',
          message: 'Profile',
        });
      case '/organization/manage':
        return i18n._({
          id: 'shared.layout.DoctorMobileHeader.manage-organization',
          message: 'Manage Organization',
        });
      default:
        return null;
    }
  }, [i18n, locale, normalizedPathname]);

  return (
    <header
      className={cn(
        // Positioning
        'sticky top-0 z-40',
        // Layout
        'flex items-center justify-between gap-2',
        'h-[calc(3.5rem+env(safe-area-inset-top,0px))] px-3 sm:px-4',
        // Styling
        'bg-background/85 backdrop-blur-xl',
        'border-b border-border/40',
        // Safe area for iOS notch
        'pt-safe',
        className
      )}
      role='banner'
    >
      {/* Logo / Home link */}
      <Link
        to={localizePath(resolveDoctorPath('/dashboard'), locale)}
        className={cn(
          'flex items-center gap-2 shrink-0',
          'focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-brand-blue focus-visible:ring-offset-2',
          'rounded-lg -ml-1 p-1'
        )}
        aria-label={t({
          id: 'shared.layout.DoctorMobileHeader.go-to-dashboard',

          message: 'Go to dashboard',
        })}
      >
        <img
          src='/Icon_Jema.svg'
          alt={t({
            id: 'shared.layout.DoctorMobileHeader.jema',

            message: 'Jema',
          })}
          className='h-7 w-7 sm:h-8 sm:w-8'
        />
      </Link>
      {/* Page title - shown on larger mobile screens */}
      {pageTitle && (
        <span
          className={cn(
            'hidden xs:block',
            'text-sm font-medium text-muted-foreground',
            'truncate flex-1 text-center'
          )}
        >
          {pageTitle}
        </span>
      )}
      {/* Right Actions */}
      <div className='flex items-center gap-0.5 shrink-0'>
        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* User Menu Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className={cn(
                'gap-1 pl-1 pr-1.5',
                'h-9 min-w-9', // Touch target
                'focus-visible:ring-brand-blue'
              )}
              aria-label={t({
                id: 'shared.layout.DoctorMobileHeader.user-menu',

                message: 'User menu',
              })}
            >
              <Avatar className='h-7 w-7 border border-border/50'>
                <AvatarImage src={userInfo.avatarSrc} alt={userInfo.displayName} />
                <AvatarFallback
                  className={cn('text-xs font-medium', 'bg-brand-blue/10 text-brand-blue')}
                >
                  {userInfo.initials}
                </AvatarFallback>
              </Avatar>
              <ChevronDown
                className='h-3 w-3 text-muted-foreground hidden sm:block'
                aria-hidden='true'
              />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align='end' className='w-56' sideOffset={8}>
            {/* User Info */}
            <DropdownMenuLabel className='font-normal'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium leading-none truncate'>{userInfo.displayName}</p>
                <p className='text-xs text-muted-foreground leading-none truncate'>
                  {userInfo.email}
                </p>
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {/* Quick Access Items (not in bottom nav) */}
            <DropdownMenuItem
              onClick={() => handleNavigate(resolveDoctorPath('/calendar'))}
              className='cursor-pointer'
            >
              <Calendar className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.DoctorMobileHeader.calendar'>Calendar</Trans>
            </DropdownMenuItem>

            {isOwner && (
              <DropdownMenuItem
                onClick={() => handleNavigate(resolveDoctorPath('/organization/manage'))}
                className='cursor-pointer'
              >
                <Building className='mr-2 h-4 w-4' aria-hidden='true' />
                <Trans id='shared.layout.DoctorMobileHeader.manage-organization'>
                  Manage Organization
                </Trans>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem disabled className='cursor-not-allowed opacity-60'>
              <Bell className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.DoctorMobileHeader.notifications'>Notifications</Trans>
              {unreadCount > 0 ? (
                <span className='ml-auto text-xs text-muted-foreground'>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              ) : null}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => handleNavigate(resolveDoctorPath('/settings'))}
              className='cursor-pointer'
            >
              <Settings className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.DoctorMobileHeader.settings'>Settings</Trans>
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => handleNavigate(resolveDoctorPath('/doctor-profile'))}
              className='cursor-pointer'
            >
              <User className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.DoctorMobileHeader.profile'>Profile</Trans>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={handleSignOut}
              className={cn(
                'cursor-pointer',
                'text-destructive focus:text-destructive',
                'focus:bg-destructive/10'
              )}
            >
              <LogOut className='mr-2 h-4 w-4' aria-hidden='true' />
              <Trans id='shared.layout.DoctorMobileHeader.sign-out'>Sign out</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
